/**
 * Wire contract of phoneservice `/phoneservice/v3/cli-verification/…`, the proof that the customer
 * controls the landline they want to signal as caller ID.
 *
 * Copied from the phoneservice DTOs on `master` (the `model` packages under `cliverification`), not from the
 * OpenAPI document: its response schemas are empty. Re-read the DTOs before trusting a value that
 * is not listed here — every one selects different copy on screen.
 */

export type CliNumberValidationOutcome =
	| 'OK'
	| 'ALREADY_VERIFIED_FOR_CUSTOMER'
	| 'NOT_ELIGIBLE_NO_SIP_TRUNK'
	| 'NOT_GERMAN'
	| 'MOBILE'
	| 'PREMIUM_OR_SERVICE'
	| 'NOMADIC'
	| 'NOT_FIXED_LINE'
	| 'INCOMPLETE'
	| 'UNAVAILABLE';

export interface CliNumberValidationResponse {
	outcome: CliNumberValidationOutcome;
	phoneNumber: string | null;
}

export type CliVerificationTriggerOutcome =
	| 'TRIGGERED'
	| 'NOT_ELIGIBLE_NO_SIP_TRUNK'
	| 'NUMBER_INELIGIBLE'
	| 'DECLARATION_MISSING'
	| 'ALREADY_VERIFIED'
	| 'LOCKED'
	| 'ATTEMPT_IN_FLIGHT'
	| 'RATE_LIMITED_COOLING_OFF'
	| 'RATE_LIMITED_NUMBER'
	| 'RATE_LIMITED_ACCOUNT'
	| 'RATE_LIMITED_GLOBAL'
	| 'IMPERSONATED_SESSION'
	| 'CALL_NOT_PLACED'
	| 'UNAVAILABLE';

export const CLI_VERIFICATION_TRIGGER_OUTCOMES: readonly CliVerificationTriggerOutcome[] = [
	'TRIGGERED',
	'NOT_ELIGIBLE_NO_SIP_TRUNK',
	'NUMBER_INELIGIBLE',
	'DECLARATION_MISSING',
	'ALREADY_VERIFIED',
	'LOCKED',
	'ATTEMPT_IN_FLIGHT',
	'RATE_LIMITED_COOLING_OFF',
	'RATE_LIMITED_NUMBER',
	'RATE_LIMITED_ACCOUNT',
	'RATE_LIMITED_GLOBAL',
	'IMPERSONATED_SESSION',
	'CALL_NOT_PLACED',
	'UNAVAILABLE',
];

export type CliVerificationAttemptStatus =
	| 'IN_FLIGHT'
	| 'ANSWERED'
	| 'VERIFIED'
	| 'WRONG_CODE_EXHAUSTED'
	| 'LOCKED_OUT'
	| 'NOT_ANSWERED'
	| 'BUSY'
	| 'REJECTED'
	| 'VOICEMAIL'
	| 'HUNG_UP'
	| 'FAILED_TO_CONNECT'
	| 'TECHNICAL_FAILURE'
	| 'CANCELLED'
	| 'TIMED_OUT';

export const CLI_VERIFICATION_ATTEMPT_STATUSES: readonly CliVerificationAttemptStatus[] = [
	'IN_FLIGHT',
	'ANSWERED',
	'VERIFIED',
	'WRONG_CODE_EXHAUSTED',
	'LOCKED_OUT',
	'NOT_ANSWERED',
	'BUSY',
	'REJECTED',
	'VOICEMAIL',
	'HUNG_UP',
	'FAILED_TO_CONNECT',
	'TECHNICAL_FAILURE',
	'CANCELLED',
	'TIMED_OUT',
];

/** Recorded on the attempt as the originating front end; the server cannot tell portal and app apart. */
export const CLI_VERIFICATION_CHANNEL = 'PORTAL';

export interface CliVerificationTriggerRequest {
	phoneNumber: string;
	channel: typeof CLI_VERIFICATION_CHANNEL;
	/** The declaration sentence exactly as it was displayed next to the ticked checkbox. */
	declarationText: string;
}

export interface CliVerificationTriggerResponse {
	outcome: CliVerificationTriggerOutcome;
	phoneNumber: string | null;
	/** TRIGGERED only. */
	attemptId?: number | null;
	/** TRIGGERED only. A live credential: keep it out of stores, logs and URLs. */
	code?: string | null;
	/** TRIGGERED only. */
	status?: CliVerificationAttemptStatus | null;
	/** TRIGGERED only. Berlin local time without offset. */
	expiresAt?: string | null;
	/** TRIGGERED only. Berlin local time without offset; always later than `expiresAt`. */
	deadline?: string | null;
	/** LOCKED and RATE_LIMITED_* only — never on ATTEMPT_IN_FLIGHT. Berlin local time without offset. */
	retryAfter?: string | null;
}

/** Poll and cancel answer with the same shape. It never carries the code. */
export interface CliVerificationAttemptResponse {
	attemptId: number;
	status: CliVerificationAttemptStatus;
	phoneNumber: string | null;
	expiresAt: string | null;
	deadline: string | null;
	failedEntries: number;
	remainingCalls: number;
	remainingTotal: number;
	/** Present only while the number is locked out. Berlin local time without offset. */
	lockedUntil?: string | null;
}

/** The backend rejects longer notes with HTTP 400. */
export const CLI_MANUAL_REQUEST_NOTE_MAX_LENGTH = 1000;

export interface CliVerificationManualRequestRequest {
	phoneNumber: string;
	note: string;
}

export interface CliVerificationManualRequestResponse {
	phoneNumber: string;
	receivedAt: string;
}

/** Verification state of the configured number as the callnote payload reports it. */
export type CliVerificationStatus = 'NONE' | 'PENDING' | 'VERIFIED';
export type CliVerificationMethod = 'VOICEBOT' | 'GRANDFATHERED';

const BERLIN_LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;

/**
 * Reads the API's offset-less `LocalDateTime` as Europe/Berlin wall-clock time.
 *
 * `new Date('2026-09-21T14:35:00')` would read it in the BROWSER's zone, so a customer abroad
 * would see a lock lifting at the wrong hour. The Berlin offset is found by asking `Intl` what
 * Berlin's clock shows for a first guess and correcting by the difference, which also lands
 * correctly on both sides of a DST switch.
 */
export function parseBerlinLocalDateTime(value: string | null | undefined): Date | null {
	if (!value) {
		return null;
	}
	const match = BERLIN_LOCAL_PATTERN.exec(value);
	if (!match) {
		return null;
	}
	const [, y, mo, d, h, mi, s] = match;
	const wallClockAsUtc = Date.UTC(+y, +mo - 1, +d, +h, +mi, s ? +s : 0);
	const offset = berlinOffsetMs(new Date(wallClockAsUtc));
	const firstGuess = wallClockAsUtc - offset;
	const corrected = wallClockAsUtc - berlinOffsetMs(new Date(firstGuess));
	return new Date(corrected);
}

function berlinOffsetMs(instant: Date): number {
	const parts = new Intl.DateTimeFormat('en-GB', {
		timeZone: 'Europe/Berlin',
		hourCycle: 'h23',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	}).formatToParts(instant);
	const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value);
	const berlinAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
	return berlinAsUtc - Math.floor(instant.getTime() / 1000) * 1000;
}
